import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import IconButton from '../../atoms/IconButton';
import NavArrowButton from '../../atoms/NavArrowButton';
import ConfirmDeleteDialog from '../../molecules/ConfirmDeleteDialog';
import useImageLightbox from '../../../hooks/useImageLightbox';
import trashIcon from '../../../images/trash.svg';
import './index.scss';

const PHOTO_DRAG_CLICK_SUPPRESSION_MS = 350;
export const PHOTO_POINTER_ACTIVATION_CONSTRAINT = { distance: 6 };
const GRID_ITEM_SIZE_CLASS = {
  lg: 'media-photo-gallery__grid-item--lg',
  md: 'media-photo-gallery__grid-item--md',
  tall: 'media-photo-gallery__grid-item--tall',
};

const DragHandleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="4" y1="8" x2="20" y2="8" />
    <line x1="4" y1="16" x2="20" y2="16" />
  </svg>
);

const resolvePhotoSource = (photo) => (
  photo?.url || photo?.imageUrl || photo?.displayUrl || photo?.previewUrl || photo?.thumb || ''
);

const resolvePhotoThumb = (photo, source) => (
  photo?.thumb || photo?.thumbnailUrl || photo?.imageUrl || source || ''
);

const buildPhotoLightboxItem = (photo, index) => {
  const src = resolvePhotoSource(photo);
  if (!src) {
    return null;
  }
  const fallbackId = `media-photo-${index}`;
  const caption = typeof photo?.caption === 'string' ? photo.caption.trim() : '';
  return {
    id: photo?.id || fallbackId,
    src,
    thumb: resolvePhotoThumb(photo, src),
    alt: photo?.alt || caption || `Media photo ${index + 1}`,
  };
};

const buildPhotoActionLabel = (baseLabel, photo) => {
  const caption = typeof photo?.caption === 'string' ? photo.caption.trim() : '';
  return caption ? `${baseLabel}: ${caption}` : baseLabel;
};

const getGridItemSizeClass = (photo) => GRID_ITEM_SIZE_CLASS[photo?.size || photo?.span] || '';

function PhotoCell({
  photo,
  index,
  isEditing,
  isDragging,
  dragAttributes,
  dragListeners,
  setActivatorNodeRef,
  onClick,
  onDelete,
  deleteLabel,
  disabled,
}) {
  const source = resolvePhotoSource(photo);
  const thumb = resolvePhotoThumb(photo, source);
  const captionLabel = typeof photo?.caption === 'string' ? photo.caption : '';
  const classes = [
    'media-photo-gallery__photo-item',
    getGridItemSizeClass(photo),
    isEditing ? 'media-photo-gallery__photo-item--editing' : '',
    isDragging ? 'media-photo-gallery__photo-item--dragging' : '',
  ].filter(Boolean).join(' ');
  const photoHref = source || '#';
  const isDragActivator = Boolean(dragListeners);
  const CellComponent = isDragActivator ? 'button' : 'a';
  const cellProps = {
    ref: setActivatorNodeRef,
    className: 'media-photo-gallery__cell',
    style: thumb ? { backgroundImage: `url(${JSON.stringify(thumb)})` } : undefined,
    draggable: false,
    onDragStart: (event) => event.preventDefault(),
    ...(dragAttributes || {}),
    ...(dragListeners || {}),
    'aria-label': isDragActivator
      ? buildPhotoActionLabel('Reorder photo', photo)
      : photo?.alt || captionLabel || 'Photo',
    onClick: (event) => {
      if (isDragActivator) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick(event, photo, index);
    },
  };

  if (isDragActivator) {
    cellProps.type = 'button';
  } else {
    cellProps.href = photoHref;
  }

  return (
    <div className={classes}>
      <CellComponent {...cellProps}>
        {isEditing ? (
          <span className="media-photo-gallery__photo-drag-hint" aria-hidden="true">
            <DragHandleIcon width="16" height="16" />
          </span>
        ) : null}
        {photo?.caption ? (
          <span className="media-photo-gallery__cap">{photo.caption}</span>
        ) : null}
      </CellComponent>
      {isEditing ? (
        <div className="media-photo-gallery__photo-actions">
          <IconButton
            icon={<img src={trashIcon} alt="" className="media-photo-gallery__delete-icon" />}
            label={buildPhotoActionLabel(deleteLabel, photo)}
            variant="ghost"
            size={34}
            className="media-photo-gallery__photo-action media-photo-gallery__photo-action--danger"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDelete?.(photo);
            }}
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}

PhotoCell.propTypes = {
  photo: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool,
  isDragging: PropTypes.bool,
  dragAttributes: PropTypes.object,
  dragListeners: PropTypes.object,
  setActivatorNodeRef: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  deleteLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

function SortablePhotoCell(props) {
  const { photo, index } = props;
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id || `media-photo-${index}` });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={['media-photo-gallery__photo-sortable', getGridItemSizeClass(photo)].filter(Boolean).join(' ')}
    >
      <PhotoCell
        {...props}
        isDragging={isDragging}
        dragAttributes={attributes}
        dragListeners={listeners}
        setActivatorNodeRef={setActivatorNodeRef}
      />
    </div>
  );
}

SortablePhotoCell.propTypes = {
  photo: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export default function MediaPhotoGallery({
  photos = [],
  isEditing = false,
  onPhotoClick,
  onDeletePhoto,
  onReorderPhotos,
  deletingPhotoId = null,
  isReorderingPhotos = false,
  deletePhotoLabel = 'Delete photo',
  previousPhotosLabel = 'Scroll photos left',
  nextPhotosLabel = 'Scroll photos right',
}) {
  const { t } = useTranslation();
  const { lightboxEl, openWithItems } = useImageLightbox({
    variant: 'fullscreen',
    tone: 'dark',
    showThumbnails: true,
  });
  // Deleting a photo is irreversible, so the trash button opens a confirmation
  // rather than calling straight through. Guarding here — at the control —
  // rather than in each consumer means no gallery can ship without it.
  const [photoPendingDeletion, setPhotoPendingDeletion] = useState(null);
  const photoList = useMemo(() => (Array.isArray(photos) ? photos : []), [photos]);
  const photoLightboxItems = useMemo(
    () => photoList.map(buildPhotoLightboxItem).filter(Boolean),
    [photoList],
  );
  const galleryScrollerRef = useRef(null);
  const suppressPhotoClickRef = useRef(false);
  const photoClickSuppressionTimerRef = useRef(null);
  const [galleryScrollState, setGalleryScrollState] = useState({
    canScrollPrevious: false,
    canScrollNext: false,
  });
  const galleryRows = photoList.length > 4 ? 2 : 1;
  const galleryColumnCount = Math.max(1, Math.ceil(photoList.length / galleryRows));
  const photoIds = useMemo(() => (
    photoList.map((photo, index) => photo?.id || `media-photo-${index}`)
  ), [photoList]);
  const isPhotoSortingEnabled = Boolean(
    isEditing
      && photoList.length > 1
      && typeof onReorderPhotos === 'function',
  );
  const photoSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: PHOTO_POINTER_ACTIVATION_CONSTRAINT }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const updateGalleryScrollState = useCallback(() => {
    const scroller = galleryScrollerRef.current;
    if (!scroller) {
      setGalleryScrollState({ canScrollPrevious: false, canScrollNext: false });
      return;
    }
    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const nextState = {
      canScrollPrevious: scroller.scrollLeft > 1,
      canScrollNext: scroller.scrollLeft < maxScrollLeft - 1,
    };
    setGalleryScrollState((current) => (
      current.canScrollPrevious === nextState.canScrollPrevious
      && current.canScrollNext === nextState.canScrollNext
        ? current
        : nextState
    ));
  }, []);
  useEffect(() => {
    updateGalleryScrollState();
    const scroller = galleryScrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    scroller.addEventListener('scroll', updateGalleryScrollState, { passive: true });
    const handleResize = () => updateGalleryScrollState();
    window.addEventListener('resize', handleResize);
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateGalleryScrollState);
      resizeObserver.observe(scroller);
    }
    return () => {
      scroller.removeEventListener('scroll', updateGalleryScrollState);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [photoList.length, updateGalleryScrollState]);
  const handleGalleryScroll = useCallback((direction) => {
    const scroller = galleryScrollerRef.current;
    if (!scroller) {
      return;
    }
    const scrollAmount = Math.max(280, scroller.clientWidth * 0.85);
    scroller.scrollBy({
      left: direction === 'previous' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
    window.setTimeout(updateGalleryScrollState, 240);
  }, [updateGalleryScrollState]);
  const releasePhotoClickSuppression = useCallback(() => {
    if (photoClickSuppressionTimerRef.current) {
      window.clearTimeout(photoClickSuppressionTimerRef.current);
    }
    photoClickSuppressionTimerRef.current = window.setTimeout(() => {
      suppressPhotoClickRef.current = false;
      photoClickSuppressionTimerRef.current = null;
    }, PHOTO_DRAG_CLICK_SUPPRESSION_MS);
  }, []);
  useEffect(() => () => {
    if (photoClickSuppressionTimerRef.current) {
      window.clearTimeout(photoClickSuppressionTimerRef.current);
    }
  }, []);
  const handlePhotoDragStart = useCallback(() => {
    if (photoClickSuppressionTimerRef.current) {
      window.clearTimeout(photoClickSuppressionTimerRef.current);
      photoClickSuppressionTimerRef.current = null;
    }
    suppressPhotoClickRef.current = true;
  }, []);
  const handlePhotoDragCancel = useCallback(() => {
    releasePhotoClickSuppression();
  }, [releasePhotoClickSuppression]);
  const handlePhotoDragEnd = useCallback((event) => {
    const { active, over } = event;
    releasePhotoClickSuppression();
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = photoIds.indexOf(active.id);
    const newIndex = photoIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onReorderPhotos?.(arrayMove(photoList, oldIndex, newIndex));
  }, [onReorderPhotos, photoIds, photoList, releasePhotoClickSuppression]);
  const handlePhotoClick = useCallback((event, photo, index) => {
    if (suppressPhotoClickRef.current) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1)) {
      return;
    }

    event?.preventDefault?.();
    const photoId = photo?.id || `media-photo-${index}`;
    const lightboxIndex = photoLightboxItems.findIndex((item) => item.id === photoId);
    if (lightboxIndex >= 0) {
      onPhotoClick?.(photo, index);
      openWithItems(photoLightboxItems, lightboxIndex);
    }
  }, [onPhotoClick, openWithItems, photoLightboxItems]);
  const requestPhotoDeletion = useCallback((photo) => {
    if (typeof onDeletePhoto !== 'function' || !photo) {
      return;
    }
    setPhotoPendingDeletion(photo);
  }, [onDeletePhoto]);
  const cancelPhotoDeletion = useCallback(() => {
    setPhotoPendingDeletion(null);
  }, []);
  // Hand off and close: the consumer owns the request, and reports progress
  // through `deletingPhotoId` (which disables the controls) and its own error
  // surface.
  const confirmPhotoDeletion = useCallback(() => {
    if (!photoPendingDeletion) {
      return;
    }
    const photo = photoPendingDeletion;
    setPhotoPendingDeletion(null);
    onDeletePhoto?.(photo);
  }, [onDeletePhoto, photoPendingDeletion]);

  if (!photoList.length) {
    return null;
  }

  const gallery = (
    <div
      className="media-photo-gallery__gallery"
      style={{
        '--media-photo-gallery-rows': String(galleryRows),
        '--media-photo-columns': String(galleryColumnCount),
      }}
    >
      {photoList.map((photo, i) => (
        isPhotoSortingEnabled ? (
          <SortablePhotoCell
            key={photo.id || i}
            photo={photo}
            index={i}
            isEditing={isEditing}
            onClick={handlePhotoClick}
            onDelete={requestPhotoDeletion}
            deleteLabel={deletePhotoLabel}
            disabled={isReorderingPhotos || Boolean(deletingPhotoId)}
          />
        ) : (
          <PhotoCell
            key={photo.id || i}
            photo={photo}
            index={i}
            isEditing={isEditing}
            onClick={handlePhotoClick}
            onDelete={requestPhotoDeletion}
            deleteLabel={deletePhotoLabel}
            disabled={isReorderingPhotos || Boolean(deletingPhotoId)}
          />
        )
      ))}
    </div>
  );
  const hasGalleryOverflow = galleryScrollState.canScrollPrevious || galleryScrollState.canScrollNext;

  return (
    <>
      <div className="media-photo-gallery__shell">
        {hasGalleryOverflow ? (
          <div className="media-photo-gallery__controls">
            <NavArrowButton
              direction="previous"
              ariaLabel={previousPhotosLabel}
              className="media-photo-gallery__nav"
              onClick={() => handleGalleryScroll('previous')}
              disabled={!galleryScrollState.canScrollPrevious}
            />
            <NavArrowButton
              direction="next"
              ariaLabel={nextPhotosLabel}
              className="media-photo-gallery__nav"
              onClick={() => handleGalleryScroll('next')}
              disabled={!galleryScrollState.canScrollNext}
            />
          </div>
        ) : null}
        <div className="media-photo-gallery__scroll" ref={galleryScrollerRef}>
          {isPhotoSortingEnabled ? (
            <DndContext
              sensors={photoSensors}
              collisionDetection={closestCenter}
              onDragStart={handlePhotoDragStart}
              onDragCancel={handlePhotoDragCancel}
              onDragEnd={handlePhotoDragEnd}
            >
              <SortableContext items={photoIds} strategy={rectSortingStrategy}>
                {gallery}
              </SortableContext>
            </DndContext>
          ) : gallery}
        </div>
      </div>
      <ConfirmDeleteDialog
        isOpen={Boolean(photoPendingDeletion)}
        title={t('mediaPhotoGallery.confirmDelete.title')}
        description={t('mediaPhotoGallery.confirmDelete.description')}
        targetName={photoPendingDeletion?.caption || null}
        onConfirm={confirmPhotoDeletion}
        onCancel={cancelPhotoDeletion}
      />
      {lightboxEl}
    </>
  );
}

MediaPhotoGallery.propTypes = {
  photos: PropTypes.array,
  isEditing: PropTypes.bool,
  onPhotoClick: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onReorderPhotos: PropTypes.func,
  deletingPhotoId: PropTypes.string,
  isReorderingPhotos: PropTypes.bool,
  deletePhotoLabel: PropTypes.string,
  previousPhotosLabel: PropTypes.string,
  nextPhotosLabel: PropTypes.string,
};
